import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

/** Builds a bullet-separated list of names, appending a `+ N` remainder when truncated. */
export const buildNameList = (
  names: string[],
  visibleNamedCount: number,
  totalCount: number
): string => {
  const namedCount = names.length;
  if (namedCount === 0) return '';
  if (visibleNamedCount >= namedCount) return names.join(' • ');
  return `${names.slice(0, visibleNamedCount).join(' • ')} + ${totalCount - visibleNamedCount}`;
};

/** @deprecated Prefer {@link buildNameList} — kept as an alias for existing call sites/tests. */
export const buildListOfShows = buildNameList;

export const buildShowsCountPrefix = (showCount: number): string =>
  `${showCount} show${showCount === 1 ? '' : 's'}`;

export const buildNetworksCountPrefix = (networkCount: number): string =>
  `${networkCount} network${networkCount === 1 ? '' : 's'}`;

type BuildCountPrefix = (count: number) => string;

/** Returns the largest number of titles that fit within `maxWidth`. */
export const maxVisibleTitles = (
  count: number,
  names: string[],
  maxWidth: number,
  buildPrefix: BuildCountPrefix,
  measurePrefixAndSuffix: (prefix: string, suffix: string) => number
): number => {
  const namedCount = names.length;
  if (namedCount === 0) return 0;
  if (maxWidth <= 0) return namedCount;

  const prefixText = buildPrefix(count);

  for (let visibleCount = namedCount; visibleCount >= 1; visibleCount--) {
    const body = buildNameList(names, visibleCount, count);
    const suffixNormal = body ? `: ${body}` : '';
    if (measurePrefixAndSuffix(prefixText, suffixNormal) <= maxWidth) return visibleCount;
  }

  return 1;
};

/** Show-specific wrapper around {@link maxVisibleTitles}. */
export const maxVisibleShowTitles = (
  showCount: number,
  showNames: string[],
  maxWidth: number,
  measurePrefixAndSuffix: (prefix: string, suffix: string) => number
): number =>
  maxVisibleTitles(showCount, showNames, maxWidth, buildShowsCountPrefix, measurePrefixAndSuffix);

let canvasCtx: CanvasRenderingContext2D | null = null;

/** Measures the combined pixel width of two text runs using Canvas */
const measurePrefixAndSuffix = (
  prefixBold: string,
  suffixNormal: string,
  strongEl: HTMLElement,
  paragraphEl: HTMLElement
): number => {
  canvasCtx ??= document.createElement('canvas').getContext('2d');
  if (!canvasCtx) return 0;

  canvasCtx.font = getComputedStyle(strongEl).font;
  const boldWidth = canvasCtx.measureText(prefixBold).width;

  canvasCtx.font = getComputedStyle(paragraphEl).font;
  const normalWidth = canvasCtx.measureText(suffixNormal).width;

  return boldWidth + normalWidth;
};

export const useTitleList = (
  count: number,
  names: string[],
  buildPrefix: BuildCountPrefix
): {
  lineRef: RefObject<HTMLParagraphElement>;
  countRef: RefObject<HTMLElement>;
  description: string;
  isTruncated: boolean;
} => {
  const lineRef = useRef<HTMLParagraphElement>(null);
  const countRef = useRef<HTMLElement>(null);
  const [visibleNamedCount, setVisibleNamedCount] = useState(() => names.length);

  const recompute = useCallback(() => {
    const paragraph = lineRef.current;
    const strong = countRef.current;
    const namedCount = names.length;

    if (namedCount === 0) {
      setVisibleNamedCount(0);
      return;
    }
    if (!paragraph || !strong) return;

    const maxWidth = paragraph.clientWidth;
    if (maxWidth <= 0) {
      setVisibleNamedCount(namedCount);
      return;
    }

    const nextVisibleCount = maxVisibleTitles(
      count,
      names,
      maxWidth,
      buildPrefix,
      (prefixBold, suffixNormal) => measurePrefixAndSuffix(prefixBold, suffixNormal, strong, paragraph)
    );
    setVisibleNamedCount(nextVisibleCount);
  }, [buildPrefix, count, names]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useLayoutEffect(() => {
    const paragraph = lineRef.current;
    if (!paragraph) return;
    const resizeObserver = new ResizeObserver(() => recompute());
    resizeObserver.observe(paragraph);
    return () => resizeObserver.disconnect();
  }, [recompute]);

  const description = useMemo(
    () => buildNameList(names, visibleNamedCount, count),
    [count, names, visibleNamedCount]
  );
  const isTruncated = names.length > 0 && visibleNamedCount < names.length;

  return { lineRef, countRef, description, isTruncated };
};

export const useShowTitleList = (
  showCount: number,
  showNames: string[]
): ReturnType<typeof useTitleList> => useTitleList(showCount, showNames, buildShowsCountPrefix);

export const useNetworkTitleList = (
  networkCount: number,
  networkNames: string[]
): ReturnType<typeof useTitleList> =>
  useTitleList(networkCount, networkNames, buildNetworksCountPrefix);
